import { useEffect, useRef, useCallback } from 'react';
import { showUpdateToast } from '../utils/toast';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const VERSION_STORAGE_KEY = 'app_build_version';

interface VersionData {
  version: string;
  buildTime: string;
}

declare const __BUILD_VERSION__: string;

/**
 * Hook that checks for app updates by comparing build versions.
 * Shows a toast notification when a new version is available.
 */
export const useVersionCheck = () => {
  const hasShownToast = useRef(false);
  const currentVersion =
    typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev';

  const checkForUpdate = useCallback(async () => {
    // Skip in development mode
    if (import.meta.env.DEV) return;

    try {
      // Fetch version.json with cache-busting query param
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) return;

      const data: VersionData = await response.json();
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

      // First visit - store the version
      if (!storedVersion) {
        localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
        return;
      }

      // Check if server has a newer version than what we loaded with
      if (data.version !== currentVersion && !hasShownToast.current) {
        hasShownToast.current = true;
        showUpdateToast();
      }
    } catch (error) {
      // Silently fail - don't break the app if version check fails
      console.debug('[Version Check] Failed to check for updates:', error);
    }
  }, [currentVersion]);

  useEffect(() => {
    // Store current version on mount
    if (import.meta.env.PROD) {
      localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
    }

    // Initial check after a short delay
    const initialTimeout = setTimeout(checkForUpdate, 3000);

    // Periodic checks
    const intervalId = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);

    // Check on tab focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        hasShownToast.current = false; // Allow showing toast again on tab focus
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check on online event (user reconnects)
    const handleOnline = () => {
      checkForUpdate();
    };
    window.addEventListener('online', handleOnline);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkForUpdate, currentVersion]);
};
